import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks.ts';
import { useValidateFixingMutation } from '@/store/orders/fixing/api/index.ts';
import { selectFixingTile } from '@/store/orders/fixing/selectors.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

export type FixingContextContent = {
  tileId: string;
  isChecking: boolean;
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
  validateFixing: (updatedProperties: Partial<FixingOrder['properties']>) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
    | null
  >;
};

type FixingValidateFn = (
  updatedProperties: Partial<FixingOrder['properties']>,
) => Promise<
  | { data: ValidateOrderResponse; error: undefined }
  | { data: undefined; error: FetchBaseQueryError | SerializedError }
  | undefined
>;

type FixingModifyingFieldContextContent = {
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
};

const FixingTileIdContext = createContext('');
const FixingIsCheckingContext = createContext(false);
const FixingValidateContext = createContext<FixingValidateFn>(async (_updatedProperties) => undefined);
const FixingModifyingFieldContext = createContext<FixingModifyingFieldContextContent>({
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const FixingContext = createContext<FixingContextContent>({
  tileId: '',
  isChecking: false,
  validateFixing: async (_updatedProperties: Partial<FixingOrder['properties']>) => undefined,
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const FixingProvider = (props: { tileId: string; children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isModifyingField, setIsModifyingField] = useState(false);

  const { children, tileId } = props;
  const [validateFixingMutation] = useValidateFixingMutation({
    selectFromResult: () => ({}),
  });
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { tile, preferencesNotificationEmails } = useAppSelector((state) => {
    const userPreferences = state.userPreferences;

    if (isNotDefined(userPreferences)) {
      console.error('[useValidateFixing] User preferences not found in state.');
      return {};
    }

    const tile = selectFixingTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[useValidateFixing] Tile with id ${tileId} not found in state.`);
      return {};
    }

    if (tile.type !== 'FixingOrder') {
      console.error(`[useValidateFixing] Tile with id ${tileId} is not a FixingOrder.`);
      return {};
    }

    return {
      tile,
      preferencesNotificationEmails: userPreferences.splitNotifications
        ? userPreferences.splitNotificationsEmailsOrders
        : userPreferences.emails,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one handleValidateFixing request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const handleValidateFixing = useCallback(
    async (updatedProperties: Partial<FixingOrder['properties']>) => {
      return queue.add(async () => {
        if (isNotDefined(tile)) {
          console.error(`[handleValidateFixing] Tile with id ${tileId} not found in state.`);
          return null;
        }

        const currency1 = updatedProperties.currencyPair?.currency1 ?? tile.properties.currencyPair?.currency1;
        const currency2 = updatedProperties.currencyPair?.currency2 ?? tile.properties.currencyPair?.currency2;

        if (isNotDefined(clientId)) {
          console.error('[handleValidateFixing] client id is not defined');
          return null;
        }

        if (isNotDefined(currency1) || isNotDefined(currency2)) {
          console.error(`[handleValidateFixing] Invalid currency pair: ${currency1}/${currency2}`);
          return null;
        }

        const updatedFixing = {
          ...tile,
          properties: {
            ...tile.properties,
            ...updatedProperties,
          },
        };

        const notificationEmails = normalizeEmailsList(
          updatedFixing.properties.notificationEmails ?? preferencesNotificationEmails ?? [],
        );

        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        const data = await validateFixingMutation({
          tileId,
          currency1,
          currency2,
          clientId,
          way: updatedFixing.properties.clientWay,
          notional: updatedFixing.properties.notional,
          notionalCurrencyIndex: updatedFixing.properties.notionalCurrencyIndex,
          fixingReference: updatedFixing.properties.fixingReference,
          ...(updatedFixing.properties.isStandardGroup
            ? { fixingTime: updatedFixing.properties.fixingTime }
            : { fixingPlace: updatedFixing.properties.fixingPlace }),
          fixingPriceType: updatedFixing.properties.isCcyForcedBidAsk
            ? 'BIDASK'
            : updatedFixing.properties.fixingPriceType,
          fixingDateUtc: updatedFixing.properties.fixingDateUtc,
          expiryDay: updatedFixing.properties.expiryDay,
          commercialMargin:
            updatedFixing.properties.fixingMarginType === 'MarginInBps'
              ? updatedFixing.properties.commercialMargin
              : undefined,
          fixingMarginType: updatedFixing.properties.fixingMarginType,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.notificationEmails),
            emails: notificationEmails,
          },
          clockOffset: updatedFixing.properties.clockOffset,
        });

        clearTimeout(checkTimer);
        setIsChecking(false);

        return data;
      });
    },
    [tile, preferencesNotificationEmails, tileId, validateFixingMutation, clientId, queue],
  );

  const validateFixing = useCallback<FixingValidateFn>(
    async (updatedProperties) => {
      const result = await handleValidateFixing(updatedProperties);

      if (isNotDefined(result)) {
        console.error('[validate] Validation result is undefined');
        return undefined;
      }

      if ('data' in result && result.data !== undefined) {
        return { data: result.data, error: undefined };
      }

      if ('error' in result && result.error !== undefined) {
        return { data: undefined, error: result.error };
      }

      return undefined;
    },
    [handleValidateFixing],
  );

  const modifyingFieldContent = useMemo(
    () => ({
      isModifyingField,
      setIsModifyingField,
    }),
    [isModifyingField],
  );

  const content = useMemo(
    () => ({
      tileId,
      isChecking,
      validateFixing,
      ...modifyingFieldContent,
    }),
    [isChecking, modifyingFieldContent, tileId, validateFixing],
  );

  return (
    <FixingTileIdContext.Provider value={tileId}>
      <FixingIsCheckingContext.Provider value={isChecking}>
        <FixingValidateContext.Provider value={validateFixing}>
          <FixingModifyingFieldContext.Provider value={modifyingFieldContent}>
            <FixingContext.Provider value={content}>{children}</FixingContext.Provider>
          </FixingModifyingFieldContext.Provider>
        </FixingValidateContext.Provider>
      </FixingIsCheckingContext.Provider>
    </FixingTileIdContext.Provider>
  );
};

export function useFixingTileId() {
  return useContext(FixingTileIdContext);
}

export function useFixingIsChecking() {
  return useContext(FixingIsCheckingContext);
}

export function useValidateFixing() {
  return useContext(FixingValidateContext);
}

export function useFixingIsModifyingField() {
  return useContext(FixingModifyingFieldContext).isModifyingField;
}

export function useFixingSetIsModifyingField() {
  return useContext(FixingModifyingFieldContext).setIsModifyingField;
}
